import React, { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../helpers/society'
import { DropdownSelect } from '../../common/DropdownSelect'
import { PostAvatar } from '../Post/PostAvatar'
import { PostAuthor } from '../Post/PostAuthor'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { DropdownItemContent } from '../DropdownItemContent'
import { FormControl } from '../../common/FormControl'
import { userQueries } from '../../../api/users'
import { societyQueries } from '../../../api/societies'
import { mediaQueries } from '../../../api/media'
import { getUserFullName, getUserInitials } from '../../../helpers/user'
import { i18nSocietyType } from '../../../i18n'
import {
  PostAuthor as PostAuthorRow,
  PostRole,
} from '../../../generated/api/post-row'

interface RoleOptionsDropdownProps {
  roles: PostRole[]
  author: PostAuthorRow
  societyType?: string
  societyCoverUrl?: string
  value?: string
  onChange?: (value: string) => void
  showLabel?: boolean
  fieldName?: string
}

export const RoleOptionsDropdown = ({
  roles,
  author,
  societyType,
  societyCoverUrl,
  value,
  onChange,
  showLabel = true,
  fieldName,
}: RoleOptionsDropdownProps): ReactNode => {
  const { translate } = useAppTranslation()

  if (roles.length < 2) {
    return null
  }

  const context = i18nSocietyType(societyType)
  const roleTitle = (role: PostRole): string => {
    switch (role) {
      case 'admin':
      case 'admin-society':
        return translate('postCreateUpdateBase.roleSheet.admin', { context })
      case 'board-member':
      case 'board-society':
        return translate('postCreateUpdateBase.roleSheet.board', { context })
      default:
        return author.fullName
    }
  }

  const dropdownOptions = roles.map((role) => ({
    value: role,
    label: roleTitle(role),
    customText: (
      <PostAuthor
        role={role}
        fullName={author.fullName}
        societyType={societyType}
      />
    ),
    image: (
      <PostAvatar
        role={role}
        author={author}
        society={{ coverUrl: societyCoverUrl }}
        avatarSize="sm"
      />
    ),
  }))

  const dropdown = (
    <DropdownSelect
      value={value}
      options={dropdownOptions}
      onChange={(val: unknown) => {
        if (typeof val === 'string') {
          onChange?.(val)
        }
      }}
      dropdownWidth="w-full md:w-60"
      dropdownContentWidth="w-fit"
      dropdownPosition="bottom-right"
      renderItemContent={(option) => <DropdownItemContent option={option} />}
    />
  )

  return showLabel ? (
    <FormControl
      name={fieldName || 'role'}
      label={translate('common.form.labels.from')}
    >
      {dropdown}
    </FormControl>
  ) : (
    dropdown
  )
}

interface RoleDropdownProps {
  watchSociety: string
  userId: string
  value?: string
  onChange?: (value: string) => void
  showLabel?: boolean
  fieldName?: string
  excludeResident?: boolean
}

export const RoleDropdown = ({
  watchSociety,
  userId,
  value,
  onChange,
  showLabel = true,
  fieldName,
  excludeResident = false,
}: RoleDropdownProps): ReactNode => {
  const { data: postSociety, isLoading: isLoadingSociety } = useQuery(
    societyQueries.getSociety(watchSociety)
  )
  const { data: cover } = useQuery(
    mediaQueries.getMedia(postSociety?.coverPhotoId ?? undefined)
  )
  const { data: user } = useQuery(userQueries.getUser(userId))
  const { data: avatar } = useQuery(
    mediaQueries.getMedia(user?.avatarId ?? undefined)
  )

  if (isLoadingSociety || !user) {
    return null
  }

  const isBoardMember = isUserBoardMemberInSociety(postSociety, userId)
  const isAdmin = isUserAdminInSociety(postSociety, userId)

  const roles: PostRole[] = [
    ...(excludeResident || postSociety?.residentPostingDisabledByAdmin
      ? []
      : (['resident'] as const)),
    ...(isAdmin ? (['admin', 'admin-society'] as const) : []),
    ...(isBoardMember ? (['board-member', 'board-society'] as const) : []),
  ]

  return (
    <RoleOptionsDropdown
      roles={roles}
      author={{
        id: user._id,
        fullName: getUserFullName(user),
        initials: getUserInitials(user),
        avatarUrl: avatar?.url,
      }}
      societyType={postSociety?.societyTypeDisplayName}
      societyCoverUrl={cover?.url}
      value={value}
      onChange={onChange}
      showLabel={showLabel}
      fieldName={fieldName}
    />
  )
}
